/**
 * Loot Config
 *
 * Drop rates, loot vacuum behavior, gold drop ranges, and pickup tuning.
 * Per-mob loot tables live in `loot.gen.ts` (TOML-sourced) — this config
 * sets the global feel/timing of pickups, not what specifically drops.
 *
 * Two-section file (defaults+dev-override pattern). See
 * `docs/plans/2026-04-25-CONFIG-MIGRATION.md`.
 */

export interface LootConfig {
  pickup: {
    /**
     * Authoritative pixel radius for a nearby-loot vacuum request.
     * The server measures from its own player position; clients never supply
     * a position, radius, or list of loot ids.
     */
    vacuumRangePx: number;
    /** Maximum number of nearby drops one vacuum request may collect. */
    vacuumBatchMax: number;
    /** Pixels-per-second the loot moves toward the player while magnetized. */
    snappingSpeedPxPerSec: number;
    /** Pixel distance at which loot is considered picked up. */
    pickupDistancePx: number;
    /** Pixel radius for the inner magnet pull. */
    magnetRadiusPx: number;
    /** Milliseconds for the bob animation cycle. */
    bobDurationMs: number;
    /** Pixel amplitude of the bob animation. */
    bobOffsetPx: number;
    /** Sparkle particle lifespan in ms. */
    sparkleLifespanMs: number;
    /** Milliseconds before unclaimed loot despawns from the world. */
    despawnTimeMs: number;
  };
  drops: {
    /** Probability (0–1) that a mob drops gold on death. */
    goldDropRate: number;
    /** Min gold amount in a single drop. */
    goldAmountMin: number;
    /** Max gold amount in a single drop. */
    goldAmountMax: number;
    /** Pixel radius within which dropped items scatter from the death point. */
    lootScatterRadiusPx: number;
  };
  presentation: {
    /** Initial world-arrival lift settled during the land phase. */
    landLiftPx: number;
    landDurationMs: number;
    landStartScale: number;
    /** Forward leg of the idle yoyo (full cycle is twice this value). */
    idleBobLegDurationMs: number;
    idleBobOffsetPx: number;
    shimmerLegDurationMs: number;
    shimmerAlphaMin: number;
    shimmerAlphaMax: number;
    /** Owner-only magnet after the authoritative result names a drop. */
    collectDurationMs: number;
    collectTargetOffsetYPx: number;
    collectEndScale: number;
    /** Neutral fade for another player's pickup, despawn, or reconciliation. */
    neutralRemovalDurationMs: number;
    despawnRemovalDurationMs: number;
    despawnSinkYPx: number;
    reconciliationRemovalDurationMs: number;
    /** Essential acknowledgement fade used when non-essential motion is reduced. */
    reducedMotionFadeMs: number;
    /** A pickup's name shows only while the player is within this distance. */
    labelNearPx: number;
    labelFadeMs: number;
  };
}

export const LOOT_DEFAULTS: LootConfig = {
  pickup: {
    vacuumRangePx: 300,
    vacuumBatchMax: 12,
    snappingSpeedPxPerSec: 400,
    pickupDistancePx: 15,
    magnetRadiusPx: 60,
    bobDurationMs: 1500,
    bobOffsetPx: 8,
    sparkleLifespanMs: 800,
    despawnTimeMs: 300_000, // 5 minutes
  },
  drops: {
    goldDropRate: 0.6,
    goldAmountMin: 1,
    goldAmountMax: 3,
    lootScatterRadiusPx: 40,
  },
  presentation: {
    landLiftPx: 10,
    landDurationMs: 110,
    landStartScale: 0.86,
    idleBobLegDurationMs: 900,
    idleBobOffsetPx: 4,
    shimmerLegDurationMs: 1100,
    // World look (2026-10-08): the halo behind a pickup breathes between these.
    shimmerAlphaMin: 0.3,
    shimmerAlphaMax: 0.6,
    collectDurationMs: 220,
    collectTargetOffsetYPx: 18,
    collectEndScale: 0.35,
    neutralRemovalDurationMs: 110,
    despawnRemovalDurationMs: 260,
    despawnSinkYPx: 8,
    reconciliationRemovalDurationMs: 70,
    reducedMotionFadeMs: 70,
    labelNearPx: 150,
    labelFadeMs: 100,
  },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to LOOT_DEFAULTS.
 *
 * Examples:
 *   pickup: { vacuumRangePx: 1000 }      // wide-area pickup for capture footage
 *   drops: { goldDropRate: 1, goldAmountMin: 100, goldAmountMax: 100 }  // farming QA
 */
export type LootConfigOverride = {
  [Section in keyof LootConfig]?: Partial<LootConfig[Section]>;
};

export const LOOT_DEV_OVERRIDE: LootConfigOverride = {};

export const LOOT: LootConfig = {
  pickup: { ...LOOT_DEFAULTS.pickup, ...LOOT_DEV_OVERRIDE.pickup },
  drops: { ...LOOT_DEFAULTS.drops, ...LOOT_DEV_OVERRIDE.drops },
  presentation: { ...LOOT_DEFAULTS.presentation, ...LOOT_DEV_OVERRIDE.presentation },
};
